import React, { useRef } from "react";
import LocationWrapper from "@/components/us-website/LocationWrapper";
import MetaTags from "@/components/seo/meta-tags";
import ElixirHero from "@/components/us-website/ElixirHero/ElixirHero";
import ElixirStats from "@/components/us-website/ElixirStats/ElixirStats";
import HowElixirSetsApart from "@/components/us-website/HowElixirSetsApart/HowElixirSetsApart";
import ElixirJourney from "@/components/us-website/ElixirJourney/ElixirJourney";
import ShippingStack from "@/components/us-website/ShippingStack/ShippingStack";
import ElixirImproves from "@/components/us-website/ElixirImproves/ElixirImproves";
import ElixirTestimonial from "@/components/us-website/ElixirTestimonial/ElixirTestimonial";
import FAQSection from "@/components/us-website/FAQSection/FAQSection";
import Resources from "@/components/us-website/Resources/Resources";
import ContactUsForm from "@/components/us-website/ContactUsForm/ContactUsForm";
import ContactUsModal from "@/components/Modal/ContactUs";
import { getNewsLetterServerSide } from "@/api/EventsApi";

const DEMO_OPTIONS = [{ id: "1", label: "Request a Elixir demo" }];

const ELIXIR_FAQS = [
  {
    question:
      "How does Elixir use AI to improve procurement in the chemical industry?",
    answer:
      "AI for procurement in the chemical industry means automating the sourcing, pricing, supplier discovery, and order management processes that traditionally require large procurement teams. Elixir's AI surfaces the right chemical products, grades, and verified suppliers instantly reducing time-to-order and procurement costs significantly for manufacturers globally.",
  },
  {
    question: "How does Elixir use AI for supply chain optimisation?",
    answer:
      "Elixir applies AI across every layer of the supply chain — from live pricing and fulfilment options to real-time shipment tracking — so teams can plan, source, and move product with full visibility and fewer manual touchpoints.",
  },
  {
    question: "How does Elixir's technology help chemical manufacturers?",
    answer:
      "Manufacturers get plain-language product discovery, grade-specific pricing, automated enquiries, and end-to-end tracking in one platform — cutting the busywork of procurement and logistics so teams focus on production.",
  },
  {
    question: "Is Elixir's AI platform built for chemical industry distributors?",
    answer:
      "Yes. Distributors use Elixir to track shipments, manage documentation, and access transparent pricing across grades and ports, giving their customers faster answers and reliable delivery timelines.",
  },
  {
    question:
      "How does Elchemy's improve supply chain transparency in the chemical industry?",
    answer:
      "Every milestone, document, and price is captured and displayed in real time, so buyers, manufacturers, and distributors all see the same source of truth — eliminating guesswork and the endless status chase.",
  },
];

function OurTechnology({ newsletterData, newsletterError }) {
  const demoModalRef = useRef();
  const openDemo = () => demoModalRef?.current?.setOpen(true);

  return (
    <LocationWrapper>
      <MetaTags
        title="Elchemy Tech | Advanced Chemical Manufacturing Technology"
        description="Discover Elchemy's cutting-edge technology platform for chemical manufacturing. Streamlined operations, improved communication, and faster service delivery."
        includeAlternateLink={true}
      />
      <div>
        <ElixirHero onRequestDemo={openDemo} />
        <ElixirStats />
        <HowElixirSetsApart />
        <ElixirJourney />
        <ShippingStack />
        <ElixirImproves />
        <ElixirTestimonial />
        <FAQSection
          faqs={ELIXIR_FAQS}
          industryName="AI Technology for Chemical Manufacturing"
          description="Everything you need to know about AI in the chemical industry, supply chain optimisation, and procurement automation."
          backgroundColor="transparent"
        />
        <Resources newsletterData={newsletterData} error={newsletterError} />
        <ContactUsForm />
      </div>

      <ContactUsModal
        ref={demoModalRef}
        heading={"Book A Demo"}
        title={"Book A Demo"}
        subHeading={"Fill in the details required to get the sample."}
        options={DEMO_OPTIONS}
        service="1"
        classes={
          "w-[163px] ml-[-2rem] sm:ml-[0] text-[#FFFFFF] h-[48px] bg-red rounded-[4px] font-medium text-[16px]"
        }
      />
    </LocationWrapper>
  );
}

export async function getServerSideProps() {
  try {
    const newsletterData = await getNewsLetterServerSide();
    return {
      props: {
        newsletterData: {
          blogdata: newsletterData?.blogdata || [],
          eventdata: newsletterData?.eventdata || [],
        },
        newsletterError: newsletterData ? null : "Failed to load content",
      },
    };
  } catch (error) {
    console.error("Error in elchemy-tech getServerSideProps:", error);
    return {
      props: {
        newsletterData: { blogdata: [], eventdata: [] },
        newsletterError: "Failed to load content",
      },
    };
  }
}

export default OurTechnology;
